import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";

import Widget from "@arcgis/core/widgets/Widget";

import {
  messageBundle,
  storeNode,
  tsx,
} from "@arcgis/core/widgets/support/widget";

import "./RightSidebar.scss";
import NavBarViewModel from "../../ui/NavBar/NavBarViewModel";
import AppT9N from "../../../t9n/App/App.json";
import { VNode } from "src/interfaces/interfaces";
import { PRODUCT_NAME } from "../Sidebar/FeaturedSidebar";
import { substitute } from "@arcgis/core/intl";

import NewBlogPic from "../../../../public/assets/featuredArticle/instant-apps-what-is-new.png";
import BlogPic from "../../../../public/assets/featuredArticle/instant-apps-blog.jpg";
import { assignPopoverId } from "../PopoverOnboarding/PopoverViewModel";

interface ISidebarTitle {
  title: string;
  useDesc: boolean;
  desc?: string;
  descTooltip?: string;
}

interface IBlogTile {
  imgRef: string;
  headerTitle: string;
  descText: string;
  linkUrl: string;
  linkText: string;
}

@subclass("Landing.widgets.RightSidebar")
export default class RightSidebar extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof AppT9N;

  @property()
  navbarVM: NavBarViewModel;

  @property()
  sidebarCloseBtn: HTMLCalciteButtonElement;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  postInitialize() {
    this.navbarVM.watch(["rightSidebarSignal"], () => {
      if (this.navbarVM.rightSidebarSignal === true) {
        setTimeout(() => {
          this.sidebarCloseBtn?.focus();
          this.sidebarCloseBtn?.setFocus();
        }, 250);
      }
    });
  }

  render() {
    return this.navbarVM.navigationRoute === "featured" ? (
      <div
        key="right-sidebar-area"
        id="right-sidebar-area"
        class={this.classes(
          "right-sidebar-area",
          this.navbarVM.rightSidebarSignal ? "right-sidebar-area-expanded" : ""
        )}
      >
        {this.navbarVM.rightSidebarSignal && this._sidebarRouter()}
      </div>
    ) : (
      <div></div>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _sidebarRouter(): VNode {
    const {
      whatsNewText,
      whatsNewDesc,
      whatsNewLink,
      blogText,
      blogDesc,
      blogLink,
    } = this.appMessages.featuredTab;
    return [
      this._renderTitle({
        title: substitute(this.appMessages.featuredTab.sidebarTitle, {
          productName: PRODUCT_NAME,
        }),
        useDesc: false,
      }),
      <br />,
      <div class="blog-tiles" id={assignPopoverId(9)}>
        {this._renderBlogTile({
          imgRef: NewBlogPic,
          headerTitle: substitute(whatsNewText, {
            productName: PRODUCT_NAME,
          }),
          descText: substitute(whatsNewDesc, { productName: PRODUCT_NAME }),
          linkUrl: "https://links.esri.com/agol-help/whats-new-instant-apps",
          linkText: whatsNewLink,
        })}
        {this._renderBlogTile({
          imgRef: BlogPic,
          headerTitle: substitute(blogText, { productName: PRODUCT_NAME }),
          descText: substitute(blogDesc, { productName: PRODUCT_NAME }),
          linkUrl:
            "https://www.esri.com/arcgis-blog/?s=#&products=instant-apps",
          linkText: substitute(blogLink, { productName: PRODUCT_NAME }),
        })}
      </div>,
    ];
  }

  private _renderBlogTile(blogTile: IBlogTile): VNode {
    const { imgRef, headerTitle, descText, linkUrl, linkText } = blogTile;
    return (
      <div class="blog-tile">
        <img src={imgRef} alt="" />
        <header class="blogHeaderText">{headerTitle}</header>
        <p>{descText}</p>
        <calcite-link href={linkUrl} target="_blank" icon-end="launch">
          {linkText}
        </calcite-link>
      </div>
    );
  }

  private _renderTitle(titleInfo: ISidebarTitle) {
    const { title, useDesc, desc, descTooltip } = titleInfo;
    const toggleVis = () => {
      this.navbarVM.toggleRightSidebarSignal();
    };
    return (
      <div id="sugg-title">
        <div>
          <h2>{title}</h2>
          {useDesc && (
            <p>
              {desc}
              &nbsp;
              <calcite-icon
                scale="s"
                tabindex="0"
                icon="information"
                class="info-icon"
                id="info-icon"
              ></calcite-icon>
              <calcite-tooltip
                label={descTooltip}
                placement="bottom"
                reference-element="info-icon"
              >
                {descTooltip}
              </calcite-tooltip>
            </p>
          )}
        </div>

        <div class="x-btn">
          <calcite-button
            onclick={toggleVis}
            icon-start="x"
            kind="neutral"
            appearance="transparent"
            afterCreate={storeNode}
            bind={this}
            data-node-ref="sidebarCloseBtn"
            scale="l"
          ></calcite-button>
        </div>
      </div>
    );
  }
}
